/**
 * IFRAP-AI - Install App Prompt Component
 * Shows a popup to guide users on how to install the PWA on their device
 */

import React, { useState, useEffect } from 'react';
import { X, Smartphone, Share, Plus, MoreVertical, Download } from 'lucide-react';

const InstallAppPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if dismissed recently
    const dismissedTime = localStorage.getItem('ifrap_install_dismissed');
    if (dismissedTime) {
      const hours = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
      if (hours < 24) {
        setDismissed(true);
      }
    }

    // Detect device
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Listen for beforeinstallprompt (Chrome/Edge/Samsung)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show prompt after 3 seconds on mobile
    const timer = setTimeout(() => {
      if (!standalone && !dismissed && (isIOSDevice || isAndroidDevice)) {
        setShowPrompt(true);
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timer);
    };
  }, [dismissed]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Native install prompt (Android Chrome)
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('ifrap_install_dismissed', Date.now().toString());
    setDismissed(true);
    setShowPrompt(false);
  };

  // Don't show if already installed or dismissed
  if (isStandalone || dismissed || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="install-app-prompt">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#000091] to-[#1a1a6e] p-6 text-white relative">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-white/60 hover:text-white p-1"
            data-testid="dismiss-install-prompt"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-[#000091] font-black text-xl">IF</span>
            </div>
            <div>
              <h3 className="text-xl font-bold">Installer IFRAP-AI</h3>
              <p className="text-white/70 text-sm">Accès rapide depuis votre écran d'accueil</p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="p-6">
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-gray-700">
              <div className="w-8 h-8 bg-[#000091]/10 rounded-full flex items-center justify-center">
                <Smartphone className="w-4 h-4 text-[#000091]" />
              </div>
              <span className="text-sm">Accès instantané sans navigateur</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <div className="w-8 h-8 bg-[#E1000F]/10 rounded-full flex items-center justify-center">
                <span className="text-[#E1000F] font-bold text-xs">€</span>
              </div>
              <span className="text-sm">Compteurs en temps réel</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Download className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-sm">Fonctionne hors connexion</span>
            </div>
          </div>

          {/* Instructions based on device */}
          {isIOS ? (
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm font-medium text-gray-900 mb-3">Comment installer sur iPhone/iPad :</p>
              <ol className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-[#000091] text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <span>Appuyez sur</span>
                  <Share className="w-4 h-4 text-[#007AFF]" />
                  <span>en bas de l'écran</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-[#000091] text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <span>Faites défiler et appuyez sur</span>
                  <Plus className="w-4 h-4 p-0.5 border border-gray-400 rounded" />
                  <span className="font-medium">"Sur l'écran d'accueil"</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-[#000091] text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <span>Appuyez sur <span className="font-medium">"Ajouter"</span></span>
                </li>
              </ol>
            </div>
          ) : isAndroid ? (
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm font-medium text-gray-900 mb-3">Comment installer sur Android :</p>
              {deferredPrompt ? (
                <p className="text-sm text-gray-600">
                  Cliquez sur le bouton ci-dessous pour installer l'application.
                </p>
              ) : (
                <ol className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-[#000091] text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <span>Appuyez sur</span>
                    <MoreVertical className="w-4 h-4" />
                    <span>en haut à droite</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-[#000091] text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <span>Sélectionnez <span className="font-medium">"Installer l'application"</span></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-[#000091] text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <span>Confirmez en appuyant sur <span className="font-medium">"Installer"</span></span>
                  </li>
                </ol>
              )}
            </div>
          ) : null}

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleDismiss}
              className="flex-1 py-3 px-4 text-gray-600 font-medium rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Plus tard
            </button>
            {deferredPrompt ? (
              <button
                onClick={handleInstallClick}
                className="flex-1 py-3 px-4 bg-[#000091] text-white font-medium rounded-xl hover:bg-[#000091]/90 transition-colors flex items-center justify-center gap-2"
                data-testid="install-app-button"
              >
                <Download className="w-4 h-4" />
                Installer
              </button>
            ) : (
              <button
                onClick={handleDismiss}
                className="flex-1 py-3 px-4 bg-[#000091] text-white font-medium rounded-xl hover:bg-[#000091]/90 transition-colors"
              >
                J'ai compris
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default InstallAppPrompt;
